import axios from 'axios'

// vedi https://github.com/SimpleJWT/drf-SimpleJWT-Vue
const BASE_URL = import.meta.env.VITE_API
const ACCESS_TOKEN = 'access_token'
const REFRESH_TOKEN = 'refresh_token'
const AUTH_ERROR_CODES = {
  USERNAME_ALREADY_EXISTS: 'USERNAME_ALREADY_EXISTS',
  INVALID_USERNAME: 'INVALID_USERNAME'
}

const tokenRequest = axios.create({
  baseURL: BASE_URL,
  timeout: 5000,
  headers: {
    'Content-Type': 'application/json',
    accept: 'application/json'
  }
})

const loginUser = (username, password) => {
  console.log(BASE_URL);
  const loginBody = { username, password }
  return tokenRequest
    .post('/api/token/', loginBody)
    .then((response) => {
      window.localStorage.setItem(ACCESS_TOKEN, response.data.access)
      window.localStorage.setItem(REFRESH_TOKEN, response.data.refresh)
      return Promise.resolve(response.data)
    })
    .catch((error) => {
      console.log(error)
      return Promise.reject(error)
    })
}

const signinUser = ({
  username,
  password,
  password_repeat,
  first_name,
  last_name,
  email,
  ragione_sociale,
  indirizzo,
  localita,
  provincia,
  stato,
  cap,
  tel,
  fax,
  web,
  cell,
  tipo_cliente,
  captcha
}) => {
  if (password !== password_repeat) {
    const mismatchError = new Error('Le password inserite non coincidono')
    mismatchError.isHandled = true
    return Promise.reject(mismatchError)
  }
  const signinBody = {
    username,
    password,
    password_repeat,
    first_name,
    last_name,
    email,
    ragione_sociale,
    indirizzo,
    localita,
    provincia,
    stato,
    cap,
    tel,
    fax,
    web,
    cell,
    tipo_cliente,
    captcha
  }
  return tokenRequest
    .post('/api/register/', signinBody)
    .then(() => ({ username, password }))
    .catch((error) => {
      const responseData = error?.response?.data ?? {}
      const appCode = responseData?.code
      const message = responseData?.message ?? error.message ?? 'Errore sconosciuto'

      if (appCode === AUTH_ERROR_CODES.USERNAME_ALREADY_EXISTS) {
        const knownError = new Error(message)
        knownError.code = AUTH_ERROR_CODES.USERNAME_ALREADY_EXISTS
        knownError.isHandled = true
        return Promise.reject(knownError)
      }

      const normalizedMessage = String(message)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()

      // Fallback retrocompatibile per backend non ancora allineati con response.data.code
      if (
        !appCode &&
        error?.response?.status === 400 &&
        normalizedMessage.includes('utente con questo nome e gia presente')
      ) {
        const knownError = new Error(message)
        knownError.code = AUTH_ERROR_CODES.USERNAME_ALREADY_EXISTS
        knownError.isHandled = true
        return Promise.reject(knownError)
      }

      if (
        error?.response?.status === 400 &&
        normalizedMessage.includes('immetti un nome utente valido')
      ) {
        const invalidUsernameError = new Error(message)
        invalidUsernameError.code = AUTH_ERROR_CODES.INVALID_USERNAME
        invalidUsernameError.isHandled = true
        return Promise.reject(invalidUsernameError)
      }

      const genericError = new Error(message)
      if (appCode) {
        genericError.code = appCode
      }
      return Promise.reject(genericError)
    })
}

const refreshToken = () => {
  const refreshBody = { refresh: window.localStorage.getItem(REFRESH_TOKEN) }
  return tokenRequest
    .post('/api/token/refresh/', refreshBody)
    .then((response) => {
      window.localStorage.setItem(ACCESS_TOKEN, response.data.access)
      return Promise.resolve(response.data)
    })
    .catch((error) => Promise.reject(error))
}

const isCorrectRefreshError = (status) => status === 401

/*
 * authRequest
 *
 * This refreshes the request and retries the token if it is invalid.
 * This is what you use to create any requests that need the Tokens.
 * Reference: https://hackernoon.com/110percent-complete-jwt-authentication-with-django-and-react-2020-iejq34ta
 *
 * Example:
 *     authRequest.get('/path/to/endpoint/',extraParameters)
 *        .then(response=>{
 *          // do something with successful request
 *        }).catch((error)=> {
 *          // handle any errors.
 *        });
 */
const authRequest = axios.create({
  baseURL: BASE_URL,
  timeout: 5000,
  headers: {
    Authorization: `Bearer ${window.localStorage.getItem(ACCESS_TOKEN)}`,
    'Content-Type': 'application/json'
  }
})

const logoutUser = () => {
  window.localStorage.removeItem(ACCESS_TOKEN)
  window.localStorage.removeItem(REFRESH_TOKEN)
  authRequest.defaults.headers.Authorization = ''
}

const errorInterceptor = (error) => {
  const originalRequest = error.config
  const { status } = error.response
  if (isCorrectRefreshError(status)) {
    return refreshToken()
      .then(() => {
        const headerAuthorization = `Bearer ${window.localStorage.getItem(ACCESS_TOKEN)}`
        authRequest.defaults.headers.Authorization = headerAuthorization
        originalRequest.headers.Authorization = headerAuthorization
        return authRequest(originalRequest)
      })
      .catch((tokenRefreshError) => {
        // if token refresh fails, logout the user to avoid potential security risks.
        logoutUser()
        return Promise.reject(tokenRefreshError)
      })
  }
  return Promise.reject(error)
}

authRequest.interceptors.response.use(
  (response) => response, // this is for all successful requests.
  (error) => errorInterceptor(error) // handle the request
)

export {
  tokenRequest,
  loginUser,
  logoutUser,
  refreshToken,
  authRequest,
  signinUser,
  errorInterceptor,
  BASE_URL,
  ACCESS_TOKEN,
  REFRESH_TOKEN,
  AUTH_ERROR_CODES
}
